<template>
  <div>
    <div class="feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-if="isShowableError" :message="errorMessage" />
      </transition>
    </div>
    <div class="white-area order-complete-header__area">
      <PageHeadlineWithFourSteps
        title="購入完了"
        :active-step="flowLineContents.activeStep"
        :steps="flowLineContents.steps"
      />
    </div>
    <div class="white-area created-gift__area">
      <div class="content-inner-area">
        <div class="created-gift__icon">
          <GiftIcon />
        </div>
        <template v-if="isPersonalUse">
          <div class="complete-message__area">
            <p class="complete-message__main">ギフトが完成しました</p>
            <p class="complete-message__sub">
              購入したチケットを確認してみましょう！
            </p>
          </div>
          <div v-if="!isApp" class="check-usable-gift__area">
            <ButtonPrimary
              text="使えるギフト一覧"
              class="check-usable-gift__button--usable-gift"
              @click="onClickMoveToOtherScreen(myPageGiftsPath)"
            />
            <ButtonSecondary
              text="購入履歴"
              class="check-usable-gift__button--order-history"
              @click="onClickMoveToOtherScreen(myPageOrderHistoriesPath)"
            />
          </div>
          <ToAppArea v-else @click-app-button="onClickAppButton">
            決済は正常に完了しています。お手数ですが下記のボタンから、ご利用いただいていたgifteeアプリにお戻りいただき、ギフトの確認をお願いいたします。
          </ToAppArea>
        </template>
        <template v-else>
          <div class="complete-message__area">
            <p class="complete-message__main">ギフトを贈る準備ができました</p>
            <p class="complete-message__sub">
              作成したギフトのURLを相手に贈りましょう！
            </p>
          </div>
          <div v-if="!isApp" class="send-other-way__area">
            <SendGiftButtons
              :gift-url="giftUrl"
              :is-displayable-toggle-text-link="false"
              :is-copy-button-primary="true"
            />
          </div>
          <ToAppArea v-else @click-app-button="onClickAppButton">
            決済は正常に完了しています。お手数ですが下記のボタンから、ご利用いただいていたgifteeアプリにお戻りいただき、ギフトのURLのコピーと送信を行ってただきますようお願いいたします。
          </ToAppArea>
        </template>
        <div class="order-code__area">
          <span class="body order-code__label">注文番号</span>
          <span class="body order-code__value">{{ orderCode }}</span>
        </div>
      </div>
    </div>
    <div v-if="!isApp" class="white-area continue-to-purchase__area">
      <div class="content-inner-area">
        <p class="white-area continue-to-purchase__title">
          {{ isPersonalUse ? '続けて購入する' : '続けてギフトを贈る' }}
        </p>
        <div class="btn-multi-block continue-to-purchase__btn-multi-area">
          <button
            v-if="isPersonalUse"
            class="btn-multi btn-multi--top chevron--forward--black"
            @click="copyFromGiftCard(true)"
          >
            同じ商品を自分用に再購入する
          </button>
          <button
            v-else
            class="btn-multi btn-multi--top chevron--forward--black"
            @click="copyFromGiftCard(false)"
          >
            同じ内容でギフトカードの下書きを作成する
          </button>
          <button
            v-if="isPersonalUse"
            class="btn-multi btn-multi--inner chevron--forward--black"
            @click="copyFromGiftCard(false)"
          >
            同じ商品をギフトとして贈る
          </button>
          <button
            class="btn-multi btn-multi--bottom chevron--forward--black"
            @click="onClickMoveToOtherScreen('/')"
          >
            トップページに戻る
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import PageHeadlineWithFourSteps from '../../molecules/headlines/PageHeadlineWithFourSteps.vue'
import SendGiftButtons from '../../organism/orders/send-gift-buttons.vue'
import ButtonPrimary from '../../molecules/buttons/ButtonPrimary.vue'
import ButtonSecondary from '../../molecules/buttons/ButtonSecondary.vue'
import GiftIcon from '../../atoms/svg_icons/GiftIcon.vue'
import ToAppArea from './ToAppArea.vue'
import {
  GIFT_CARD_FLOW_STEPS,
  GIFT_CARD_FLOW_STEPS_PREVIEW_OPTIONAL
} from '../../../constants/flowLineConstants'

export default {
  components: {
    FeedbackError,
    ToAppArea,
    GiftIcon,
    ButtonSecondary,
    ButtonPrimary,
    SendGiftButtons,
    PageHeadlineWithFourSteps
  },
  props: {
    isPersonalUse: {
      type: Boolean,
      required: false
    },
    giftUrl: {
      type: String,
      required: true
    },
    orderId: {
      type: Number,
      required: true
    },
    orderCode: {
      type: String,
      required: true
    },
    myPageGiftsPath: {
      type: String,
      required: true
    },
    myPageOrderHistoriesPath: {
      type: String,
      required: true
    },
    isApp: {
      type: Boolean,
      required: false,
      default: false
    },
    orderHistoriesDeepLink: {
      type: String,
      required: true
    },
    giftCardId: {
      type: Number,
      required: true
    },
    previewOptional: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      // FIXME(gift_card_preview_optional): ABテスト決着後、previewOptionalの分岐を削除する
      flowLineContents: {
        steps: this.previewOptional
          ? GIFT_CARD_FLOW_STEPS_PREVIEW_OPTIONAL
          : GIFT_CARD_FLOW_STEPS,
        activeStep: 4
      },
      giftIconImage: '',
      errorMessage: '',
      isShowableError: false
    }
  },
  methods: {
    enterError() {
      setTimeout(() => {
        this.isShowableError = false
      }, 3000)
    },
    onClickMoveToOtherScreen(path) {
      window.location.href = path
    },
    onClickAppButton() {
      window.location.href = this.orderHistoriesDeepLink
    },
    async copyFromGiftCard(personalUseForNewGiftCard) {
      try {
        const res = await axios.post(
          '/api/v1/users/@me/gift_card_sessions/copy_from_gift_card',
          {
            gift_card_session: {
              gift_card_id: this.giftCardId,
              personal_use_for_new_gift_card: personalUseForNewGiftCard
            }
          }
        )
        dataLayer.push({
          event: 'repeat_purchase_button',
          via: 'orders_complete',
          source_gift_card_usage_way: this.isPersonalUse
            ? 'self_use'
            : 'gift_use',
          target_gift_card_usage_way: personalUseForNewGiftCard
            ? 'self_use'
            : 'gift_use',
          gift_card_session_id: res.data.gift_card_session_id,
          source_gift_card_order_id: this.orderId
        })
        location.href = personalUseForNewGiftCard
          ? `/draft_orders/${res.data.draft_order_id}`
          : '/gift_card/edit'
      } catch (error) {
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.isShowableError = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.order-complete-container {
  .created-gift {
    &__area {
      padding-top: 56px;

      @media screen and (max-width: $small-contents-width) {
        // 56px(基準のpadding or margin)/375px(基準の親要素のwidth)*100 = 14.933...
        padding-top: 14.93vw;
      }
    }

    &__icon {
      margin-bottom: 16px;

      @media screen and (max-width: $small-contents-width) {
        // 16px(基準のpadding or margin)/375px(基準の親要素のwidth)*100 = 4.266...
        margin-bottom: 4.27vw;
      }

      path {
        fill: $primary;
      }
    }
  }

  .check-usable-gift {
    &__area {
      margin-bottom: 40px;
      padding: 0 16px 0;

      @media screen and (max-width: $small-contents-width) {
        // 40px(基準のpadding or margin)/375px(基準の親要素のwidth)*100 = 10.666...
        margin-bottom: 10.67vw;
        padding-left: 4.27vw;
        padding-right: 4.27vw;
      }
    }

    &__button {
      &--usable-gift {
        margin-bottom: 24px;

        @media screen and (max-width: $small-contents-width) {
          // 24px(基準のpadding or margin)/375px(基準の親要素のwidth)*100 = 6.4
          margin-bottom: 6.4vw;
        }
      }
    }
  }

  .complete-message {
    &__area {
      margin-bottom: 24px;

      @media screen and (max-width: $small-contents-width) {
        margin-bottom: 6.4vw;
      }
    }

    &__main {
      @extend %subhead-bold;

      margin-bottom: 8px;

      @media screen and (max-width: $small-contents-width) {
        // 8px(基準のpadding or margin)/375px(基準の親要素のwidth)*100 = 2.133...
        margin-bottom: 2.13vw;
      }
    }

    &__sub {
      @extend %caption;
    }
  }

  .continue-to-purchase {
    &__area {
      margin-top: 16px;
      padding: 16px 0;

      @media screen and (max-width: $small-contents-width) {
        margin-top: 4.27vw;
        padding: 4.27vw 0;
      }
    }

    &__title {
      @extend %subhead-bold;
      text-align: left;
      margin-bottom: 16px;
      padding: 0 16px;

      @media screen and (max-width: $small-contents-width) {
        margin-bottom: 4.27vw;
        padding: 0 4.27vw;
      }
    }

    &__btn-multi-area {
      margin-bottom: 16px;
      padding: 0 16px;

      @media screen and (max-width: $small-contents-width) {
        margin-bottom: 4.27vw;
        padding: 0 4.27vw;
      }
    }
  }
}
</style>
